import type { ReactNode } from "react";
import { useTheme } from "@/hooks/useTheme";

const B = ({ children }: { children: ReactNode }) => (
  <strong className="font-medium text-[var(--color-fg)]">{children}</strong>
);

const Icon = ({ size = 14, children }: { size?: number; children: ReactNode }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    {children}
  </svg>
);

// --- SERVICE category icons ---
const ScalesIcon = () => (
  <Icon size={14}>
    <path d="M12 3v18" />
    <path d="M5 7h14" />
    <path d="M5 7l-3 7a4 4 0 0 0 8 0L7 7" />
    <path d="M19 7l-3 7a4 4 0 0 0 8 0l-3-7" />
  </Icon>
);
const ShieldIcon = () => (
  <Icon size={14}>
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </Icon>
);
const CalculatorIcon = () => (
  <Icon size={14}>
    <rect x="4" y="2" width="16" height="20" rx="2" />
    <line x1="8" y1="6" x2="16" y2="6" />
    <line x1="8" y1="11" x2="8" y2="11" />
    <line x1="12" y1="11" x2="12" y2="11" />
    <line x1="16" y1="11" x2="16" y2="11" />
    <line x1="8" y1="15" x2="8" y2="15" />
    <line x1="12" y1="15" x2="12" y2="15" />
    <line x1="16" y1="15" x2="16" y2="15" />
    <line x1="8" y1="19" x2="16" y2="19" />
  </Icon>
);
const serviceCategories: { name: string; icon: ReactNode }[] = [
  { name: "Legal", icon: <ScalesIcon /> },
  { name: "Fund Ops & Compliance", icon: <ShieldIcon /> },
  { name: "Accounting", icon: <CalculatorIcon /> },
];

// --- TEAM headshots (core team + advisors split by divider) ---
type TeamMember = { src?: string; initials?: string; name: string; objectPosition?: string; objectFit?: "cover" | "contain" };

const coreTeam: TeamMember[] = [
  { src: "/team-ben.jpg", name: "Ben Zhang", objectPosition: "center top" },
  { src: "/team-hugh.jpg", name: "Hugh Carnegie", objectPosition: "center 15%" },
  { src: "/team-yiyan.jpg", name: "Yiyan Ng" },
  { src: "/team-cher.png", name: "Cher Truman" },
];

const advisors: TeamMember[] = [
  { src: "/team-george.png", name: "George Yang" },
  { src: "/team-loo.png", name: "Cheng-Guan Loo", objectPosition: "center 25%" },
];

const Avatar = ({
  src,
  initials,
  name,
  objectPosition,
  objectFit = "cover",
  size,
  bg,
}: {
  src?: string;
  initials?: string;
  name: string;
  objectPosition?: string;
  objectFit?: "cover" | "contain";
  size: number;
  bg?: string;
}) => (
  <div
    title={name}
    className="rounded-full overflow-hidden ring-2 ring-[var(--color-bg)] border border-[var(--color-border)] flex items-center justify-center shrink-0"
    style={{ width: size, height: size, background: bg ?? "var(--color-surface)" }}
  >
    {src ? (
      <img
        src={src}
        alt={name}
        className="w-full h-full"
        style={{ objectFit, objectPosition }}
      />
    ) : (
      <span className="text-[9px] font-medium tracking-wider font-[family-name:var(--font-heading)] text-[var(--color-fg-secondary)]">
        {initials}
      </span>
    )}
  </div>
);

// --- TRACTION partner logo strip (Raymond Legal logo varies by theme) ---
type Partner = { type: "logo" | "text"; src?: string; name: string };
const buildPartners = (isDark: boolean): Partner[] => [
  { type: "logo", src: isDark ? "/partner-raymondlegal.png" : "/partner-raymondlegal-light.png", name: "Raymond Legal" },
  { type: "text", name: "Ratner" },
  { type: "logo", src: "/partner-proactive.svg", name: "ProActive Legal" },
  { type: "logo", src: "/client-turpinmiller.jpg", name: "Turpin Miller" },
  { type: "logo", src: "/client-visapositive.jpg", name: "Visa Positive" },
  { type: "logo", src: "/client-tannlaw.png", name: "Tann Law" },
];

const buildBlocks = (partners: Partner[]): {
  label: string;
  icon: ReactNode;
  body: ReactNode;
  extra: ReactNode;
}[] => [
  {
    label: "The service",
    icon: (
      <Icon>
        <rect x="2" y="7" width="20" height="14" rx="2" />
        <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
      </Icon>
    ),
    body: (
      <>
        We deploy AI agents inside professional firms and run them across legal, fund ops & compliance, and accounting. Firms get <B>completed work, not software to learn</B>.
      </>
    ),
    extra: (
      <div className="mt-3 flex items-center gap-2">
        {serviceCategories.map((c) => (
          <div key={c.name} className="flex items-center gap-1.5">
            <div className="w-[26px] h-[26px] rounded-full bg-[var(--color-surface)] border border-[var(--color-border)] flex items-center justify-center text-[var(--color-fg-tertiary)] shrink-0">
              {c.icon}
            </div>
            <span className="text-[9px] font-[family-name:var(--font-mono)] uppercase tracking-wider text-[var(--color-fg-tertiary)]">
              {c.name}
            </span>
          </div>
        ))}
      </div>
    ),
  },
  {
    label: "The market",
    icon: (
      <Icon>
        <polyline points="3 17 9 11 13 15 21 7" />
        <polyline points="14 7 21 7 21 14" />
      </Icon>
    ),
    body: (
      <>
        <B>$191B</B> in directly addressable work that&apos;s intelligence-heavy and already outsourced. <B>Nobody has built the AI-native version yet.</B>
      </>
    ),
    extra: (
      <div className="mt-3 flex items-center gap-3 h-[26px]">
        <span className="text-[9px] font-[family-name:var(--font-mono)] font-bold uppercase tracking-[0.2em] text-[var(--color-accent)]">
          Source
        </span>
        <span className="text-[10px] font-[family-name:var(--font-mono)] font-medium uppercase tracking-wider text-[var(--color-fg-secondary)]">
          Sequoia Capital · Autopilot Territory · Mar 2026
        </span>
      </div>
    ),
  },
  {
    label: "The traction",
    icon: (
      <Icon>
        <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z" />
        <path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z" />
        <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0" />
        <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5" />
      </Icon>
    ),
    body: (
      <>
        Immigration AI agent live (<B>15 clients, 3 countries</B>). Two anchor partnerships: <B>Raymond Legal</B> (UK, £2.5M practice) and <B>Ratner</B> (Singapore). Both MSA signed, agents deploying. Plus 20-50 firms in pipeline through low-cost AI services.
      </>
    ),
    extra: (
      <div className="mt-3 flex items-center gap-3 h-[26px]">
        {partners.map((p) =>
          p.type === "logo" ? (
            <img
              key={p.name}
              src={p.src}
              alt={p.name}
              title={p.name}
              className="h-full w-auto object-contain opacity-70"
            />
          ) : (
            <span
              key={p.name}
              title={p.name}
              className="text-[10px] font-[family-name:var(--font-mono)] font-medium uppercase tracking-[0.15em] text-[var(--color-fg-tertiary)] border border-dashed border-[var(--color-border)] rounded-sm px-2 py-1"
            >
              {p.name}
            </span>
          )
        )}
      </div>
    ),
  },
  {
    label: "The team",
    icon: (
      <Icon>
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </Icon>
    ),
    body: (
      <>
        Founded by <B>fintech serial entrepreneurs</B>, with team members from <B>WPP and Meta</B>. Advised by investors in <B>Anthropic, Stripe, xAI</B>, and 30+ years of PE/M&amp;A experience.
      </>
    ),
    extra: (
      <div className="mt-3 flex items-center gap-3">
        <div className="flex items-center -space-x-1.5">
          {coreTeam.map((m) => (
            <Avatar key={m.name} {...m} size={30} />
          ))}
        </div>
        <div className="w-px h-6 bg-[var(--color-border)]" />
        <div className="flex items-center -space-x-1.5">
          {advisors.map((m) => (
            <Avatar key={m.name} {...m} size={30} />
          ))}
        </div>
      </div>
    ),
  },
];

export function V3OverviewSlide({ isActive: _ }: { isActive: boolean }) {
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const blocks = buildBlocks(buildPartners(isDark));

  return (
    <div className="flex flex-col h-full">
      <h2 className="text-[32px] font-light tracking-tight mb-6 font-[family-name:var(--font-heading)]">
        The AI back-office for professional services.
      </h2>

      <div className="grid grid-cols-2 gap-x-10 gap-y-6">
        {blocks.map((b) => (
          <div key={b.label} className="flex flex-col">
            <div className="flex items-center gap-2 mb-2 pb-1.5 border-b border-[var(--color-border)] text-[var(--color-accent)]">
              <span>{b.icon}</span>
              <span className="text-[10px] font-[family-name:var(--font-mono)] font-bold uppercase tracking-[0.15em]">
                {b.label}
              </span>
            </div>
            <p className="text-[13px] text-[var(--color-fg-secondary)] leading-relaxed font-light">
              {b.body}
            </p>
            <div className="mt-auto">{b.extra}</div>
          </div>
        ))}
      </div>

      <div className="text-left mt-auto mb-8 pb-4 border-b border-[var(--color-border)]">
        <p className="text-[12px] text-[var(--color-fg-secondary)] font-light leading-relaxed">
          Raising <strong className="font-medium text-[var(--color-fg)]">~$2.7M seed</strong> to prove the playbook in legal, then <strong className="font-medium text-[var(--color-fg)]">replay it in compliance and accounting</strong>.
        </p>
      </div>
    </div>
  );
}
